import createAsyncAction from "utils/createAsyncAction";
import { SECTIONS } from "config/constants";
import { TEMPLATES, RUBRICS } from "config/constants/editor";

/**
 * Given an action that returns a promise with an API response, turn the response into the correct form for react-select.
 * @param {function} action An action that returns an API response promise
 */
export const forTypeahead = (
  action,
  openOnSecondChar = false
) => lookup => dispatch => {
  if (openOnSecondChar && lookup.length < 2) {
    return Promise.resolve();
  }

  return dispatch(action(lookup)).then(action => {
    const {
      payload: { results },
    } = action;
    return { options: results };
  });
};

export const GET_TOPIC_LIST_REQUESTED = "GET_TOPIC_LIST_REQUESTED";
export const GET_TOPIC_LIST_SUCCEEDED = "GET_TOPIC_LIST_SUCCEEDED";
export const GET_TOPIC_LIST_FAILED = "GET_TOPIC_LIST_FAILED";

export const getTopicListAsync = createAsyncAction(
  "topics",
  "list",
  "GET_TOPIC_LIST",
  ([name]) => ({ name })
);
export const getTopicListAsyncForTypeahead = lookup => dispatch => {
  return forTypeahead(getTopicListAsync)(lookup)(dispatch).then(results => {
    return {
      options: results.options.filter(
        ({ show_in_eden }) => show_in_eden !== false
      ),
    };
  });
};

export const GET_SUBTOPIC_LIST_REQUESTED = "GET_SUBTOPIC_LIST_REQUESTED";
export const GET_SUBTOPIC_LIST_SUCCEEDED = "GET_SUBTOPIC_LIST_SUCCEEDED";
export const GET_SUBTOPIC_LIST_FAILED = "GET_SUBTOPIC_LIST_FAILED";

export const getSubtopicListAsync = createAsyncAction(
  "subtopics",
  "list",
  "GET_SUBTOPIC_LIST",
  ([name]) => ({ name })
);
export const getSubtopicListAsyncForTypeahead = lookup => dispatch => {
  return forTypeahead(getSubtopicListAsync)(lookup)(dispatch).then(results => {
    return {
      options: results.options.filter(
        ({ show_in_eden }) => show_in_eden !== false
      ),
    };
  });
};

export const GET_AUTHOR_LIST_REQUESTED = "GET_AUTHOR_LIST_REQUESTED";
export const GET_AUTHOR_LIST_SUCCEEDED = "GET_AUTHOR_LIST_SUCCEEDED";
export const GET_AUTHOR_LIST_FAILED = "GET_AUTHOR_LIST_FAILED";

export const getAuthorListAsync = createAsyncAction(
  "authors",
  "list",
  "GET_AUTHOR_LIST",
  ([display_name]) => ({ display_name })
);
export const getAuthorListAsyncForTypeahead = forTypeahead(
  getAuthorListAsync,
  true
);

export const GET_RUBRIC_LIST_REQUESTED = "GET_RUBRIC_LIST_REQUESTED";
export const GET_RUBRIC_LIST_SUCCEEDED = "GET_RUBRIC_LIST_SUCCEEDED";
export const GET_RUBRIC_LIST_FAILED = "GET_RUBRIC_LIST_FAILED";

export const getRubricListAsync = createAsyncAction(
  "rubrics",
  "list",
  "GET_RUBRIC_LIST",
  ([name]) => ({ name })
);

export const getRubricListAsyncForTypeahead = lookup => dispatch => {
  return forTypeahead(getRubricListAsync)(lookup)(dispatch).then(results => {
    return { options: [RUBRICS.DEFAULT, ...results.options] };
  });
};

export const GET_TEMPLATE_LIST_REQUESTED = "GET_TEMPLATE_LIST_REQUESTED";
export const GET_TEMPLATE_LIST_SUCCEEDED = "GET_TEMPLATE_LIST_SUCCEEDED";
export const GET_TEMPLATE_LIST_FAILED = "GET_TEMPLATE_LIST_FAILED";

export const getTemplateListAsync = createAsyncAction(
  "templates",
  "list",
  "GET_TEMPLATE_LIST",
  ([name]) => ({ name })
);

export const getTemplateListAsyncForTypeahead = lookup => dispatch => {
  return forTypeahead(getTemplateListAsync)(lookup)(dispatch).then(results => {
    return { options: [TEMPLATES.DEFAULT, ...results.options] };
  });
};

/**
 * This is a function that generates a function that's meant to be used to overwrite react-select's default filterOptions()
 * For the non-primary typeaheads, this disables the primary typeahead, removes selected options, and removes Top from the menu
 * @param {string} field Either "sections" or "tags"
 * @param {string} field Either "section" or "tag", used to add (Primary section) to disabled option
 */
const filteredSectionsAndTags = (field, fieldName) => {
  return (options, fitler, currentValues) => {
    return (dispatch, getState) => {
      if (options && options.length > 0) {
        const primary = getState().editor.form[field];
        let newOptions = [...options];

        if (primary) {
          const primaryIndex = options.findIndex(
            option => option.id === primary.id
          );
          const primaryFromOptions = options[primaryIndex];
          if (primaryFromOptions) {
            const newPrimaryFromOptions = {
              ...primaryFromOptions,
              disabled: true,
              name: `${primaryFromOptions.name} (Primary ${fieldName})`,
            };
            newOptions = [newPrimaryFromOptions, ...options];
            newOptions.splice(primaryIndex + 1, 1);
          }
        }

        if (currentValues && currentValues.length > 0) {
          const currentIds = Object.values(currentValues).map(item => item.id);
          newOptions = newOptions.filter(item => {
            return !currentIds.includes(item.id);
          });
        }

        newOptions = newOptions.filter(option => {
          return option.id !== SECTIONS.TOP.id;
        });

        return newOptions;
      }
      return [];
    };
  };
};

/**
 * This is a function that generates a function that's meant to be used to overwrite react-select's default filterOptions()
 * For the primary typeaheads, this disables already selected sections or tags and removes Top from the menu
 * @param {string} field Either "sections" or "tags"
 */
export const filteredPrimarySectionsAndTags = field => {
  return options => {
    return (dispatch, getState) => {
      if (options && options.length > 0) {
        const selectedItems = getState().editor.form[field];
        let newOptions = [...options];

        if (selectedItems && selectedItems.length) {
          const selectedItemIds = Object.values(selectedItems).map(
            item => item.id
          );
          newOptions = options.map(option => {
            if (selectedItemIds.includes(option.id)) {
              option = { ...option, disabled: true };
            }
            return option;
          });
        }

        newOptions = newOptions.filter(option => {
          return option.id !== SECTIONS.TOP.id;
        });
        return newOptions;
      }
      return [];
    };
  };
};

export const GET_SECTION_LIST_REQUESTED = "GET_SECTION_LIST_REQUESTED";
export const GET_SECTION_LIST_SUCCEEDED = "GET_SECTION_LIST_SUCCEEDED";
export const GET_SECTION_LIST_FAILED = "GET_SECTION_LIST_FAILED";

export const getSectionListAsync = createAsyncAction(
  "sections",
  "list",
  "GET_SECTION_LIST",
  ([name]) => ({ name })
);

export const getSectionListAsyncForTypeahead = forTypeahead(
  getSectionListAsync
);
export const getFilteredSectionsForTypeahead = filteredSectionsAndTags(
  "primary_section",
  "section"
);
export const getFilteredPrimarySectionsForDropdown = filteredPrimarySectionsAndTags(
  "sections"
);

export const GET_TAG_LIST_REQUESTED = "GET_TAG_LIST_REQUESTED";
export const GET_TAG_LIST_SUCCEEDED = "GET_TAG_LIST_SUCCEEDED";
export const GET_TAG_LIST_FAILED = "GET_TAG_LIST_FAILED";

export const getTagListAsync = createAsyncAction(
  "tags",
  "list",
  "GET_TAG_LIST",
  ([name]) => ({ name })
);

export const getTagListAsyncForTypeahead = forTypeahead(getTagListAsync, true);
export const getFilteredTagsForTypeahead = filteredSectionsAndTags(
  "primary_tag",
  "tag"
);
export const getFilteredPrimaryTagsForDropdown = filteredPrimarySectionsAndTags(
  "tags"
);

export const GET_SUBSCRIPTION_LIST_REQUESTED =
  "GET_SUBSCRIPTION_LIST_REQUESTED";
export const GET_SUBSCRIPTION_LIST_SUCCEEDED =
  "GET_SUBSCRIPTION_LIST_SUCCEEDED";
export const GET_SUBSCRIPTION_LIST_FAILED = "GET_SUBSCRIPTION_LIST_FAILED";

export const getSubscriptionListAsync = createAsyncAction(
  "subscriptions",
  "list",
  "GET_SUBSCRIPTION_LIST",
  ([name]) => ({ name, can_send: true })
);

export const getSubscriptionListAsyncForTypeahead = forTypeahead(
  getSubscriptionListAsync
);

/**
 * Get the list of loadOptions for the featuring dropdown
 */
export const getSectionsForFeaturingDropdown = (
  options,
  fitler,
  currentValues
) => {
  return (dispatch, getState) => {
    const form = getState().editor.form;
    let sectionsToDisplay = [];

    if (form.isTopStory) {
      sectionsToDisplay.push(SECTIONS.TOP);
    }
    if (form.primary_section) {
      sectionsToDisplay.push(form.primary_section);
    }
    if (form.sections.length > 0) {
      sectionsToDisplay = [...sectionsToDisplay, ...form.sections];
    }

    if (currentValues && currentValues.length > 0) {
      const currentIds = Object.values(currentValues).map(item => item.id);
      sectionsToDisplay = sectionsToDisplay.filter(item => {
        return !currentIds.includes(item.id);
      });
    }

    return sectionsToDisplay;
  };
};
